/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';

// redux
import { useDispatch, useSelector } from "react-redux";
import { updateCachedRevenueDeduction, updateUpdateStatus, resetToDB, updateCachedRevenueDeductionForAccount } from "../../../../../state/slices/settings";
import { updateProductRevenueDeduction, updateProductRevenueDeductionForAccount } from "../../../../../state/thunks/settings";

// material
import { ListTable } from '@insticator/insticator-ui';
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import InputForm from "../../../../../material/inputform/inputForm";

// utils & consts
import {preventPasteNegative, preventSymbols} from "../../billing/advanced/utils/functions";
import {USAGE_FEES_COL_HEADS, USAGE_FEES_PRODUCT_MAPPING, USAGE_FEES_PRODUCT_INFO} from "../insticatorplatform_const";

// styles
import injectSheet from 'react-jss';
import styles from './usagefees_styles';

// const
const SECTION_TITLE = 'Usage Fees'
const UPDATE_STATUS = 'usageFeesUpdateStatus'
const UPDATE_STATUS_FOR_ACCOUNT = 'usageFeesUpdateStatusForAccount'
const STATUS_IDLE = 'idle'
const SUCCESS_MESSAGE = 'Update Success'
const ERROR_MESSAGE = 'Update Failed'
const PARTIAL_ERROR_MESSAGE_FOR_GLOBAL_FEES = 'Failed to Update Global Level Platform Fees'
const PARTIAL_ERROR_MESSAGE_FOR_ACCOUNT_LEVEL_FEES = 'Failed to Update Account Level Platform Fees'

/* ========== ~~~~~~~~~~ Usage Fees ~~~~~~~~~~ ========== */
const UsageFees = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const {
    settings: {
      productRevenueDeduction,
      productRevenueDeductionFromDB,
      productRevenueDeductionForAccount,
      productRevenueDeductionForAccountFromDB,
      usageFeesUpdateStatus,
      usageFeesUpdateStatusForAccount
    }
  } = useSelector((state) => state)
  const permissions = useSelector((state) => state.session.permissions)
  const accountUUID = useSelector((state) => state.session.accountUUID)
  const [isOpened, setIsOpened] = useState(false)
  const [isChanged, setIsChanged] = useState(false)
  const isPermissionToManageGlobalPlatformFees = permissions.includes('settings.managePlatformFees')
  const isPermissionToManageAccountPlatformFees = permissions.includes('settings.manageAccountPlatformFees')

  /*************** ASYNC ***************/
  
  useEffect(() => {
      if (usageFeesUpdateStatus === 'success' || usageFeesUpdateStatus === 'error') setTimeout(() => {
      dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    }, [2000])
  }, [usageFeesUpdateStatus])
  
  useEffect(() => {
    if (usageFeesUpdateStatusForAccount === 'success' || usageFeesUpdateStatusForAccount === 'error') setTimeout(() => {
      dispatch(updateUpdateStatus({ field: UPDATE_STATUS_FOR_ACCOUNT, status: STATUS_IDLE }))
    }, [2000])
  }, [usageFeesUpdateStatusForAccount])

  useEffect(() => {
    let newIsChanged = false
    Object.entries(productRevenueDeduction)
      .forEach(([key, value]) => {
        const cachedValue = Number(value)
        if (value === null || cachedValue === 'NaN') {
          newIsChanged = false
        } else if (Number(productRevenueDeductionFromDB[key]).toFixed(2) !== cachedValue.toFixed(2)) {
          newIsChanged = true
        }
      })
    setIsChanged(newIsChanged)
  }, [productRevenueDeduction])

  useEffect(() => {
    let newIsChanged = false
    Object.entries(productRevenueDeductionForAccount)
      .forEach(([key, value]) => {
        const cachedValue = Number(value)
        if (value === null || cachedValue === 'NaN') {
          newIsChanged = false
        } else if (Number(productRevenueDeductionForAccountFromDB[key]).toFixed(2) !== cachedValue.toFixed(2)) {
          newIsChanged = true
        }
      })
    setIsChanged(newIsChanged)
  }, [productRevenueDeductionForAccount])

  /*************** EVENT HANDLERS ***************/
  const handleToggle = () => {
    dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    dispatch(updateUpdateStatus({ field: UPDATE_STATUS_FOR_ACCOUNT, status: STATUS_IDLE }))
    setIsOpened(!isOpened)
  }

  const handleCancel = () => {
    setIsOpened(false)
    dispatch(resetToDB({ cache: 'productRevenueDeduction', db: 'productRevenueDeductionFromDB' }))
    dispatch(resetToDB({ cache: 'productRevenueDeductionForAccount', db: 'productRevenueDeductionForAccountFromDB' }))
  }

  const handleSave = () => {
    setIsOpened(false)
    const requestData = {...productRevenueDeduction}
    const requestDataForAccount = {...productRevenueDeductionForAccount}
    Object.keys(requestData).map(product => {
      requestData[product] = requestData[product] === ""
        ? 0.00
        : requestData[product] / 100.0
    })
    Object.keys(requestDataForAccount).map(product => {
      requestDataForAccount[product] = requestDataForAccount[product] === ""
        ? 0.00
        : requestDataForAccount[product] / 100.0
    })
    if(isPermissionToManageGlobalPlatformFees)
    {
      dispatch(updateProductRevenueDeduction(requestData))
    }
    else
    {
      dispatch(resetToDB({ cache: 'productRevenueDeduction', db: 'productRevenueDeductionFromDB' }))
    }
    if(isPermissionToManageAccountPlatformFees)
    {
      USAGE_FEES_PRODUCT_INFO.forEach(product => {
        if(requestDataForAccount[product.id] === undefined)
        {
          // inherit from productRevenueDeduction
          requestDataForAccount[product.id] = Number(productRevenueDeduction[product.id]) / 100
        }
      });
      dispatch(updateProductRevenueDeductionForAccount({accountUUID,requestData:requestDataForAccount}))
    }
    else
    {
      dispatch(resetToDB({ cache: 'productRevenueDeductionForAccount', db: 'productRevenueDeductionForAccountFromDB' }))
    }
  }

  const handleRevenueDeductionChange = e => {
    let val = e.target.value

    val = (val.indexOf(".") >= 0)
      ? (val.substr(0, val.indexOf(".")) + val.substr(val.indexOf("."), 3))
      : val;
    if (val > 100) {
      return
    }

    const newRevenueDeduction = Object.assign({}, productRevenueDeduction)
    const product = e.target.name
    newRevenueDeduction[product] = val
    dispatch(updateCachedRevenueDeduction(newRevenueDeduction))
  }

  const handleRevenueDeductionForAccountChange = e => {
    let val = e.target.value

    val = (val.indexOf(".") >= 0)
      ? (val.substr(0, val.indexOf(".")) + val.substr(val.indexOf("."), 3))
      : val;
    if (val > 100) {
      return
    }

    const newRevenueDeductionForAccount = Object.assign({}, productRevenueDeductionForAccount)
    const product = e.target.name
    newRevenueDeductionForAccount[product] = val
    dispatch(updateCachedRevenueDeductionForAccount(newRevenueDeductionForAccount))
  }
  /*************** DATA MAPPING & FORMATTING ***************/
  const formattedProducts = () => {
    let productMapping = JSON.parse(JSON.stringify(USAGE_FEES_PRODUCT_MAPPING))
    let uniqueAllProductMappings = new Set([...Object.keys(productRevenueDeduction) ,...Object.keys(productRevenueDeductionForAccount)])
    uniqueAllProductMappings
      .forEach(product => {
        const productInfo = USAGE_FEES_PRODUCT_INFO.filter(info => info.id === product)
        if (productInfo.length === 0) return
        productMapping[productInfo[0].section].push({
          ...productInfo[0],
          revenueDeduction: productRevenueDeduction[product],
          revenueDeductionForAccount: productRevenueDeductionForAccount[product]
        })
      })

    return Object.entries(productMapping)
      .map(([key, value]) => ({
        product:
          <div className={props.classes.product}>
            <h2 className="spacing">{key}</h2>
            <div className="productCol">
              {value.map(val => <div className="spacing">{val.name}</div>)}
            </div>
          </div>,
        usageFeeForAccount:
        <div className={props.classes.usageFee}>
          <div className="usageFeeCol">
            <div className="spacing">&nbsp;</div>
            {value.map(val => <InputForm name={val.id}
                                         disabled={!isPermissionToManageAccountPlatformFees}
                                         value={val.revenueDeductionForAccount}
                                         isNumber={true}
                                         handleChange={handleRevenueDeductionForAccountChange}
                                         handlePaste={preventPasteNegative}
                                         handleKeyPress={preventSymbols}/>)}
            </div>
        </div>,
        usageFee:
        <div className={props.classes.usageFee}>
          <div className="usageFeeCol">
            <div className="spacing">&nbsp;</div>
            {value.map(val => <InputForm name={val.id} 
                                         disabled={!isPermissionToManageGlobalPlatformFees}
                                         value={val.revenueDeduction}
                                         isNumber={true}
                                         handleChange={handleRevenueDeductionChange}
                                         handlePaste={preventPasteNegative}
                                         handleKeyPress={preventSymbols}/>)}
            </div>
        </div>
      }))
  }
  /*************** UI ***************/
  let statusMessage = "";
  switch (usageFeesUpdateStatus) {
    case "success":
      statusMessage = SUCCESS_MESSAGE
      break;
    case "failed":
      statusMessage = ERROR_MESSAGE
      break;
    case "idle":
      statusMessage = ""
      break;
  }
  let statusMessageForAccount = ""
  switch (usageFeesUpdateStatusForAccount) {
    case "success":
      statusMessageForAccount = SUCCESS_MESSAGE
      break;
    case "failed":
      statusMessageForAccount = ERROR_MESSAGE
      break;
    case "idle":
      statusMessageForAccount = ""
      break;
  }
  let messageToDisplay =  (statusMessage === SUCCESS_MESSAGE && statusMessageForAccount === SUCCESS_MESSAGE ? SUCCESS_MESSAGE :  
    (statusMessage === ERROR_MESSAGE && statusMessageForAccount === ERROR_MESSAGE ? ERROR_MESSAGE : 
      ((statusMessage === SUCCESS_MESSAGE || statusMessage === "") && statusMessageForAccount === ERROR_MESSAGE ? PARTIAL_ERROR_MESSAGE_FOR_ACCOUNT_LEVEL_FEES :
      (statusMessage === ERROR_MESSAGE && (statusMessageForAccount === SUCCESS_MESSAGE || statusMessageForAccount === "") ? PARTIAL_ERROR_MESSAGE_FOR_GLOBAL_FEES : 
      (statusMessage === SUCCESS_MESSAGE && statusMessageForAccount === "" ? SUCCESS_MESSAGE : 
      (statusMessage === "" && statusMessageForAccount === SUCCESS_MESSAGE ? SUCCESS_MESSAGE : ""))))));

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      title={SECTION_TITLE}statusMessage
      description={messageToDisplay}
      descriptionClass={messageToDisplay === SUCCESS_MESSAGE ? props.classes.updateSuccess : props.classes.updateFailed}
    /> 
  )

  return (
    <ExpandableSection
      controller={controller}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isOpened={isOpened}
      isChanged={isChanged}
      >
      {isOpened && <>
        <SectionCard>
          <ListTable
            columnHeads={USAGE_FEES_COL_HEADS}
            data={formattedProducts()}
            size={'full'}
          />
        </SectionCard>
      </>}
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default UsageFees;